import _ from 'lodash';
import { differenceInHours, parseISO } from 'date-fns';
import type { IProjectFullOrSimpleJSON, IUserJSON, IDashboardProjectJSON } from 'external-api-types';
import type { ProjectPermission } from '../types/Permission';
import config from '../../config';
import { importedProjectTypes } from '../consts';
import type { Project as WizardProject } from '../types/Wizard';
import type { StudioContainerSettings } from '../reducers/studio';
import logger from './logging/logger';
import UserService from './UserService';
import { getCoreAppUrl } from './integration.utils';

const SUPPORTED_MIME_TYPES_GENERAL = [
    'image/apng',
    'image/bmp',
    'image/gif',
    'image/x-icon',
    'image/jpeg',
    'image/avif',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/webp'
];

const SUPPORTED_MIME_TYPES_BY_CMS: Record<string, string[]> = {
    sanity: ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/tiff', 'image/webp']
};

export enum CONTAINER_STATUS {
    HIBERNATING = 'hibernating',
    STARTING = 'starting',
    RUNNING = 'running',
    RESTARTING = 'restarting',
    FAILED = 'failed',
    UNKNOWN = 'unknown'
}

export enum SSG_STATE {
    STARTING = 'starting',
    RUNNING = 'running',
    RESTARTING = 'restarting',
    FAILED_TO_START = 'failedToStart'
}

export enum BUILD_STATUS {
    DRAFT = 'draft',
    BUILDING = 'building',
    DEPLOYING = 'deploying',
    LIVE = 'live',
    FAILING = 'failing',
    BUILD_FAILED = 'build-failed',
    UNKNOWN = 'unknown'
}

enum WizardServices {
    theme = 'theme',
    ssg = 'ssg',
    cms = 'cms',
    repository = 'repository',
    deployment = 'deployment',
    container = 'container'
}

export function getContainerStatus(project: IProjectFullOrSimpleJSON | null): CONTAINER_STATUS {
    return project?.deploymentData?.container?.status ?? CONTAINER_STATUS.UNKNOWN;
}

export function getBuildStatus(project: IProjectFullOrSimpleJSON): BUILD_STATUS | string {
    // fix types on api to make it more string rather than just string
    return project?.buildStatus ?? BUILD_STATUS.UNKNOWN;
}

export function isContainerRunning(project: IProjectFullOrSimpleJSON | null): boolean {
    return [CONTAINER_STATUS.RUNNING, CONTAINER_STATUS.RESTARTING].includes(getContainerStatus(project));
}

export function isContainerColdStarting(project: IProjectFullOrSimpleJSON | null): boolean {
    return [CONTAINER_STATUS.HIBERNATING, CONTAINER_STATUS.STARTING].includes(getContainerStatus(project));
}

export function isCSIEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.widget?.csiEnabled ?? false;
}

export function hasPermission(project: Pick<IProjectFullOrSimpleJSON, 'permissions'> | null, permission: ProjectPermission): boolean {
    return hasPermissions(project, [permission]);
}

export function hasPermissions(project: Pick<IProjectFullOrSimpleJSON, 'permissions'> | null, permissions: ProjectPermission[], partial?: boolean): boolean {
    const projectPermissions = project?.permissions ?? [];
    return permissions[partial ? 'some' : 'every']((permission) => projectPermissions.includes(permission));
}

export type Tier = { id: string; name?: string | undefined; paidTierName?: string | undefined };

export function getTrialTierHook(project: IProjectFullOrSimpleJSON, hookName: string): Tier | undefined {
    return project.tier?.hooks?.[hookName]?.trialTiers.find(({ id }: { id: string }) => isTrialAvailable(project, id));
}

export function getPaidTierHook(project: IProjectFullOrSimpleJSON, hookName: string): Tier | undefined {
    return project.tier?.hooks?.[hookName]?.trialTiers[0];
}

export function getTierHook(
    project: IProjectFullOrSimpleJSON,
    hookName: string
): {
    trialUpgradeHook?: Tier;
    paidUpgradeHook?: Tier;
} {
    return {
        trialUpgradeHook: getTrialTierHook(project, hookName),
        paidUpgradeHook: getPaidTierHook(project, hookName)
    };
}

export function isTrialAvailable(project: IProjectFullOrSimpleJSON, tierId: string): boolean {
    return _.find(project?.eligibleForTrials, { id: tierId })?.eligible ?? false;
}

export function isImpersonatingProject(project: IProjectFullOrSimpleJSON | null): boolean {
    if (isCollaboratorOnProject(project)) {
        return false;
    }
    return hasPermission(project, 'STACKBIT_ADMIN_IMPERSONATE');
}

export function isCollaboratorOnProject(project: IProjectFullOrSimpleJSON | null): boolean {
    return hasPermission(project, 'COLLABORATOR');
}

export function isDeploying(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.buildStatus === 'queued' || project?.buildStatus === 'building' || project?.buildStatus === 'deploying';
}

export function isInitialDeploy(project: IProjectFullOrSimpleJSON | null): boolean {
    return (project?.metrics?.deployCount ?? 0) < 1 && project?.buildStatus === 'deploying';
}

// tstodo fix any
export function getCurrentSplitTest(project: IProjectFullOrSimpleJSON | null): $TSFixMe {
    return project?.splitTests?.[0] ?? { variants: [] };
}

// tstodo fix any
export function getCurrentSplitTestVariant(project: IProjectFullOrSimpleJSON, environment: string | null): $TSFixMe {
    const { variants } = getCurrentSplitTest(project);
    return environment ? _.find(variants, { environment }) : _.first(variants);
}

export function hasLiveSite(project: IProjectFullOrSimpleJSON): boolean {
    const deploymentId = project?.wizard?.deployment?.id;
    if (!deploymentId) {
        return false;
    }
    const netlifyUrl = project?.deploymentData?.[deploymentId]?.url;
    const deployed = project?.deployedAt;
    return !!(netlifyUrl && deployed);
}

export function isContainer(project: IProjectFullOrSimpleJSON | null): boolean {
    /**
     * container is actual deployment and should not be part of isContainer logic
     * since not all client migrated yet from container => sharedContainer it has to be here
     */
    return ['container'].includes(_.get(project, 'wizard.deployment.id', '')) || ['sharedContainer'].includes(_.get(project, 'wizard.container.id', ''));
}

export function isHealthy(project: IProjectFullOrSimpleJSON): boolean {
    return project?.deploymentData?.container?.healthy === true;
}

export function hasHMR(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.deploymentData?.container?.hasHMR ?? false;
}

export function hasLiveReload(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.deploymentData?.container?.hasLiveReload ?? false;
}

export function getHmrUrl(project: IProjectFullOrSimpleJSON | null): string {
    return project?.deploymentData?.container?.hmrUrl ?? '';
}

export function getHmrSocketType(project: IProjectFullOrSimpleJSON | null): string {
    return project?.deploymentData?.container?.hmrSocketType ?? 'socket.io';
}

export function getHmrPath(project: IProjectFullOrSimpleJSON | null, containerSettings?: StudioContainerSettings | null): string {
    const ssgId = getSsgId(project, containerSettings);
    const hmrPath = project?.deploymentData?.container?.hmrPath;
    if (hmrPath) {
        return hmrPath;
    }

    return ssgId === 'nextjs' ? 'nextjs-live-updates' : '';
}

export function isHibernating(project: IProjectFullOrSimpleJSON): boolean {
    return project?.deploymentData?.container?.hibernating;
}

export function isImportedContainer(project: IProjectFullOrSimpleJSON | null): boolean {
    return isContainer(project) && project?.wizard?.container?.settings?.imported;
}

export function isGenericSsgInContainer(project: IProjectFullOrSimpleJSON | null): boolean {
    return isContainer(project) && project?.wizard?.theme?.id === 'custom';
}

export function isCodeEditorEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return Boolean(config.features.codeEditor && project?.widget?.codeEditorEnabled);
}

export function isBranchInfoEnabled(project: IProjectFullOrSimpleJSON): boolean {
    return project?.widget?.branchInfoEnabled;
}

export function canPublish(project: IProjectFullOrSimpleJSON): boolean {
    const branchStatus = getBranchStatus(project);
    const branchInfoEnabled = isBranchInfoEnabled(project);
    const canPublish = branchInfoEnabled ? _.get(branchStatus, 'canPublish', true) : true;
    return canPublish;
}

export function getApiKey(project: IProjectFullOrSimpleJSON | null, keyName: string): string | undefined {
    const keys = project?.APIKeys ?? [];
    const keyObj = keys.find((keyDef) => keyDef.name === keyName);
    return keyObj?.key;
}

export function isCodeEditorActionsEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return Boolean(isCodeEditorEnabled(project) && project?.widget?.codeEditorActionsEnabled);
}

export function isSavePresetEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return !!project?.widget?.savePresetEnabled;
}

export function isPointerEventsHighlightsEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return !!project?.widget?.highlightsPointerEvents;
}

export function isLocalizationEnabled(project: IProjectFullOrSimpleJSON | null): boolean {
    return !!project?.widget?.localizationEnabled;
}

export function projectHasStackbitYaml(project: IProjectFullOrSimpleJSON): boolean {
    const isCustomTheme = project?.wizard?.theme?.id === 'custom';
    if (isCustomTheme) {
        return isGenericSsgInContainer(project) && project?.wizard?.theme?.settings?.stackbitYmlFound;
    }
    // if project isn't custom theme - it always has stackbit.yml
    return true;
}

export function getSiteUrl(project: IProjectFullOrSimpleJSON | IDashboardProjectJSON, user: IUserJSON, forceAppendWidget?: boolean): string {
    const emptyUrl = !project.siteUrl;
    const isImported = importedProjectTypes.includes(project?.importData?.dataType ?? '');
    // todo add widget injection flag for container project

    const hasInjectedWidget = project?.widget?.netlifyInject;
    const isContainer = project?.wizard?.container;
    const shouldAddWidget = forceAppendWidget || isContainer || (hasInjectedWidget && isImported);

    if (emptyUrl || !shouldAddWidget) {
        return project.siteUrl || '';
    }

    const url = new URL(project?.siteUrl ?? '');
    const { searchParams } = url;

    if (user?.widgetAuthToken) {
        searchParams.append('stackbit', user?.widgetAuthToken);
    } else {
        logger.error(`User doesn't have widgetAuthToken`, { userId: user.id });
    }

    url.search = `?${searchParams.toString()}`;
    return url.toString();
}

export function getProjectSettingsUrl(project: IProjectFullOrSimpleJSON | IDashboardProjectJSON, coreProjectSettingSitePath: string): string | null {
    if (!project.deploymentData?.netlify || project.wizard?.deployment?.settings.sharedUser !== false) {
        return null;
    }

    try {
        const { pathname: sitePath } = new URL(project.deploymentData.netlify.url);
        return new URL(sitePath + coreProjectSettingSitePath, getCoreAppUrl()).href;
    } catch (_) {
        return null;
    }
}

export function isDeepLinksEnabledForGit(project: IProjectFullOrSimpleJSON | null): boolean {
    const repoId = project?.wizard?.repository?.id ?? '';
    const repoData = project?.deploymentData?.[repoId];
    const transferStatus = _.get(repoData, 'transferStatus') ?? 'transferred';
    return transferStatus === 'transferred';
}

export function isRepoReadOnly(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.deploymentData?.github?.readOnly ?? false;
}

export function isContainerPreviewUrlReady(project: IProjectFullOrSimpleJSON): boolean {
    const containerUrl = project?.deploymentData?.container?.url;
    return !!containerUrl;
}

export function isLocalContainer(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.wizard?.settings?.local ?? false;
}

export function getContainerPreviewUrl(project: IProjectFullOrSimpleJSON, user: IUserJSON, path?: string): string {
    const containerUrl = project?.deploymentData?.container?.url;
    if (!containerUrl) {
        return '';
    }
    const previewId = project?.deploymentData?.container?.lastPreviewId;
    const widgetAuthToken = user?.widgetAuthToken;
    return `${containerUrl}${path || ''}?preview=${previewId}&stackbit=${widgetAuthToken}`;
}

export function getProjectThumbUrl(project: IProjectFullOrSimpleJSON | IDashboardProjectJSON): string {
    return project.thumbUrl || project.wizard?.theme?.settings?.image;
}

export function getSubscriptionPageUrl(project: IProjectFullOrSimpleJSON): string {
    return `/project/${project.id}/subscription`;
}

export function getContainerSpinupUrl(project: IProjectFullOrSimpleJSON, environmentName: string): string {
    return `/project/${project.id}/loading?env=${environmentName || ''}`;
}

export function getContainerUrl(project: IProjectFullOrSimpleJSON | null | undefined): string {
    return project?.deploymentData?.container?.url;
}

export function getContainerSocketPath(): string {
    return `/_socket/`;
}

export function getProjectRootRoute(project: IProjectFullOrSimpleJSON | null): string {
    return project?.deploymentData?.container?.rootRoute ?? '';
}

export function getContainerCodeEditorUrl(project: IProjectFullOrSimpleJSON | null): string {
    return project?.deploymentData?.container?.codeEditorUrl;
}

export function getSSGState(project: IProjectFullOrSimpleJSON | null): SSG_STATE {
    return project?.deploymentData?.container?.ssgState as SSG_STATE;
}

export function getSSGRestartNeeded(project: IProjectFullOrSimpleJSON): boolean {
    return project?.deploymentData?.container?.ssgRestartNeeded;
}

export function isContainerHibernating(project: IProjectFullOrSimpleJSON | null): boolean {
    return project?.deploymentData?.container?.hibernating;
}

export function getContentRefreshWebhook(project: IProjectFullOrSimpleJSON): string {
    return new URL(`project/${project?.id}/webhook/refresh-content`, config.api.base_url).toString();
}

export const isManualRepoTransferEnabled = (project: IProjectFullOrSimpleJSON): boolean => {
    const manualRepoTransferEnabled = config?.features?.manualRepoTransfer;
    const isSharedUser = project?.wizard?.repository?.settings?.sharedUser;
    return manualRepoTransferEnabled && isSharedUser;
};

export function hasActiveNotificationsForUser(project: IProjectFullOrSimpleJSON, user: IUserJSON): boolean {
    const hasConnectedServices = project?.availableFeatures?.settingsConnectedServices;
    const repositoryId = project?.wizard?.repository?.id ?? '';

    const transferStatus = project?.deploymentData?.[repositoryId]?.transferStatus;
    const repoTransferInitiated = transferStatus === 'initiated';
    const repoTransferTransferred = transferStatus === 'transferred';
    const manualTransferPending = isManualRepoTransferEnabled(project) && !repoTransferInitiated && !repoTransferTransferred;

    if (hasConnectedServices && (repoTransferInitiated || manualTransferPending)) {
        return true;
    }

    return !UserService.isEmailConfirmed(user);
}

export function getDefaultBranch(project: IProjectFullOrSimpleJSON): string {
    const repoId = project?.wizard?.repository?.id ?? '';
    return project?.deploymentData?.[repoId]?.defaultBranch ?? 'master';
}

// actually can return any
export function getDeploymentData(project: IProjectFullOrSimpleJSON, serviceType: WizardServices): $TSFixMe {
    const serviceId = project?.wizard?.[serviceType]?.id ?? '';
    return project?.deploymentData?.[serviceId];
}

export function getBranches(project: IProjectFullOrSimpleJSON): { previewBranch: string; publishBranch: string } {
    const publishBranch = project?.deploymentData?.container?.publishBranch ?? getDefaultBranch(project);
    const previewBranch = project?.deploymentData?.container?.previewBranch ?? publishBranch;
    return { previewBranch, publishBranch };
}

export function getBranchStatus(project: IProjectFullOrSimpleJSON): string {
    return project?.deploymentData?.container?.branchStatus;
}

export function normalizeAssetUrlForProject(assetUrl: string, project: IProjectFullOrSimpleJSON): string {
    return normalizeAssetUrl(assetUrl, getContainerUrl(project));
}

export function normalizeAssetUrl(assetUrl: string, containerUrl: string): string {
    if (assetUrl && !assetUrl.startsWith('//') && (assetUrl.startsWith('/') || !assetUrl.startsWith('http'))) {
        return new URL(assetUrl, containerUrl).toString();
    }
    return assetUrl;
}

export function getSupportedMimeTypes(cmsId?: string): string[] {
    return SUPPORTED_MIME_TYPES_BY_CMS[cmsId ?? ''] ?? SUPPORTED_MIME_TYPES_GENERAL;
}

export function getPreviewToken(project: IProjectFullOrSimpleJSON | null): string {
    return project?.previewToken ?? new URL(document.location.toString()).searchParams.get('previewToken') ?? '';
}

export function getImportType(project: IProjectFullOrSimpleJSON): string {
    return project?.wizard?.settings?.importType;
}

export function doesCmsRequireConnection(cmsId: string): boolean {
    return ['contentful', 'sanity', 'datocms', 'forestry'].includes(cmsId);
}

export function doesUserHaveCmsConnection(user: IUserJSON, cmsId: string): boolean {
    return doesCmsRequireConnection(cmsId) && !!UserService.getConnection(user, cmsId);
}

// we don't have stackbit.yaml types yet
export function getAutogeneratedStackbitYaml(wizard: IProjectFullOrSimpleJSON['wizard']): $TSFixMe {
    return wizard?.theme?.settings?.stackbitYmlFound ? null : wizard?.theme?.settings?.themeConfig;
}

export function isDeployingDashboard(project: IProjectFullOrSimpleJSON): boolean {
    const isBuilding = project.buildStatus === 'queued' || project.buildStatus === 'building' || project.buildStatus === 'deploying';
    const hours = differenceInHours(Date.now(), parseISO(project.updatedAt?.toString()));
    const isRecent = hours < 1;
    return isBuilding && isRecent;
}

export function getStudioVersion(project: IProjectFullOrSimpleJSON): number {
    return project?.wizard?.settings?.studioVersion ?? 1;
}

export const hasDeployment = (project?: IProjectFullOrSimpleJSON | null): boolean => {
    const deploymentId = project?.wizard?.deployment?.id;
    return deploymentId ? Boolean(project?.deploymentData?.[deploymentId]) : false;
};

export const getSubscriptionButtonTitle = (): string => 'Contact us';

export function legacyIsGitBasedProject(project: IProjectFullOrSimpleJSON): boolean {
    const cmsId = legacyGetCMSId(project);
    return ['git', 'forestry'].includes(cmsId);
}

export const legacyGetCMSId = (project?: IProjectFullOrSimpleJSON | WizardProject | null): string =>
    project?.wizard?.cms?.id || project?.wizard?.theme?.settings?.themeConfig?.import?.type || '';

export const getSsgId = (project?: IProjectFullOrSimpleJSON | null, containerSettings?: StudioContainerSettings | null) =>
    containerSettings?.ssgName ?? project?.wizard?.ssg?.id;
